// src/components/home/homePageComponents/CategoriesSection.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CategoryManager from "../../../services/home/CategoryManager";
import BookManager from "../../../services/home/BookManager";
import { ChevronDown, ChevronUp } from "lucide-react";
import config from "../../../config";

// Distinct photographic covers, clean unique titles, and verified database book counts for each category
const CATEGORY_MAP_BY_ID = {
  4: {
    displayName: "Fiction",
    bgImage: "https://images.unsplash.com/photo-1476275466078-4007374efbbe?auto=format&fit=crop&w=600&q=80",
    exactCount: 497,
    priority: 1,
  },
  3: {
    displayName: "Knowledge & Insights",
    bgImage: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80",
    exactCount: 587,
    priority: 2,
  },
  16: {
    displayName: "Non-Fiction",
    bgImage: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=600&q=80",
    exactCount: 178,
    priority: 3,
  },
  18: {
    displayName: "Self Help",
    bgImage: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80",
    exactCount: 124,
    priority: 4,
  },
  8: {
    displayName: "Academic & Education",
    bgImage: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=600&q=80",
    exactCount: 82,
    priority: 5,
  },
  13: {
    displayName: "Language & Literature",
    bgImage: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=600&q=80",
    exactCount: 56,
    priority: 6,
  },
  1: {
    displayName: "Fantasy",
    bgImage: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=600&q=80",
    exactCount: 40,
    priority: 7,
  },
  15: {
    displayName: "Children & Teens",
    bgImage: "https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=600&q=80",
    exactCount: 36,
    priority: 8,
  },
  35: {
    displayName: "Spiritual & Wisdom",
    bgImage: "https://images.unsplash.com/photo-1507652313519-d4e9174996dd?auto=format&fit=crop&w=600&q=80",
    exactCount: 36,
    priority: 9,
  },
  23: {
    displayName: "Marathi Literature",
    bgImage: "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?auto=format&fit=crop&w=600&q=80",
    exactCount: 30,
    priority: 10,
  },
  2: {
    displayName: "Action & Adventure",
    bgImage: "https://images.unsplash.com/photo-1533240332313-0db49b459ad6?auto=format&fit=crop&w=600&q=80",
    exactCount: 29,
    priority: 11,
  },
  9: {
    displayName: "Comics & Graphic Novels",
    bgImage: "https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&w=600&q=80",
    exactCount: 25,
    priority: 12,
  },
  22: {
    displayName: "Children's Literature",
    bgImage: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=600&q=80",
    exactCount: 29,
    priority: 13,
  },
  30: {
    displayName: "Social History & Tributes",
    bgImage: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=600&q=80",
    exactCount: 22,
    priority: 14,
  },
  28: {
    displayName: "Research & Social Studies",
    bgImage: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=600&q=80",
    exactCount: 19,
    priority: 15,
  },
  12: {
    displayName: "Hobbies & Lifestyle",
    bgImage: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=600&q=80",
    exactCount: 18,
    priority: 16,
  },
  34: {
    displayName: "Biography & Memoir",
    bgImage: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=600&q=80",
    exactCount: 15,
    priority: 17,
  },
  17: {
    displayName: "Poetry Collection",
    bgImage: "https://images.unsplash.com/photo-1474932430478-367dbb6832c1?auto=format&fit=crop&w=600&q=80",
    exactCount: 14,
    priority: 18,
  },
  27: {
    displayName: "Agriculture & Farming",
    bgImage: "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=600&q=80",
    exactCount: 14,
    priority: 19,
  },
  21: {
    displayName: "Historical Fiction",
    bgImage: "https://images.unsplash.com/photo-1461360370896-922624d12aa1?auto=format&fit=crop&w=600&q=80",
    exactCount: 13,
    priority: 20,
  },
  24: {
    displayName: "Politics & Governance",
    bgImage: "https://images.unsplash.com/photo-1541872703-74c5e44368f9?auto=format&fit=crop&w=600&q=80",
    exactCount: 11,
    priority: 21,
  },
  36: {
    displayName: "Environmental Education",
    bgImage: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=600&q=80",
    exactCount: 10,
    priority: 22,
  },
  25: {
    displayName: "Sports & Fitness",
    bgImage: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=600&q=80",
    exactCount: 9,
    priority: 23,
  },
  20: {
    displayName: "Economics & Finance",
    bgImage: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=600&q=80",
    exactCount: 8,
    priority: 24,
  },
  10: {
    displayName: "Art, Design & Craft",
    bgImage: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=600&q=80",
    exactCount: 7,
    priority: 25,
  },
  37: {
    displayName: "Social Science",
    bgImage: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=600&q=80",
    exactCount: 7,
    priority: 26,
  },
  11: {
    displayName: "Cooking & Food",
    bgImage: "https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&w=600&q=80",
    exactCount: 6,
    priority: 27,
  },
  14: {
    displayName: "Technology & Computers",
    bgImage: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=600&q=80",
    exactCount: 6,
    priority: 28,
  },
  33: {
    displayName: "Business & Startups",
    bgImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80",
    exactCount: 2,
    priority: 29,
  },
  32: {
    displayName: "Law & Policy Reports",
    bgImage: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=600&q=80",
    exactCount: 2,
    priority: 30,
  },
  38: {
    displayName: "Women's Rights",
    bgImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80",
    exactCount: 2,
    priority: 31,
  },
  19: {
    displayName: "Contemporary Fiction",
    bgImage: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=600&q=80",
    exactCount: 1,
    priority: 32,
  },
  26: {
    displayName: "Environmental Science",
    bgImage: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=600&q=80",
    exactCount: 1,
    priority: 33,
  },
  29: {
    displayName: "True Crime & Mystery",
    bgImage: "https://images.unsplash.com/photo-1509198397868-475647b2a1e5?auto=format&fit=crop&w=600&q=80",
    exactCount: 0,
    priority: 34,
  },
  40: {
    displayName: "Health & Fitness",
    bgImage: "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=600&q=80",
    exactCount: 2,
    priority: 35,
  },
  41: {
    displayName: "Fitness & Wellness",
    bgImage: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=600&q=80",
    exactCount: 1,
    priority: 36,
  },
};

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=600&q=80";

// Helper to format real quantity
const formatBookCount = (count) => {
  if (count === undefined || count === null) return "Explore books";
  if (count === 1) return "1 book";
  return `${count} books`;
};

const CategoriesSection = ({ className = "" }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // 1. Fetch categories
  useEffect(() => {
    let isMounted = true;
    const fetchCats = async () => {
      try {
        const resp = await CategoryManager.getAllCategories();
        if (isMounted && resp && resp.responseCode === 200) {
          setCategories(resp.responseData || []);
        }
      } catch (err) {
        console.error("Error fetching categories for home section:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchCats();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Fetch live category counts in background to ensure 100% database accuracy
  useEffect(() => {
    if (!categories.length) return;
    let isMounted = true;

    const fetchLiveCounts = async () => {
      try {
        const counts = {};
        await Promise.all(
          categories.map(async (cat) => {
            try {
              const res = await BookManager.getBooksByCategory(cat.catId);
              if (isMounted && Array.isArray(res)) {
                counts[cat.catId] = res.length;
              }
            } catch {
              // ignore individual errors, fallback will be used
            }
          })
        );
        if (isMounted && Object.keys(counts).length > 0) {
          setLiveCounts(counts);
        }
      } catch (err) {
        console.warn("Could not fetch live counts", err);
      }
    };

    fetchLiveCounts();

    return () => {
      isMounted = false;
    };
  }, [categories]);

  // Format and enhance categories: ensure each one has unique identity, title, and verified book count
  const enhancedCategories = useMemo(() => {
    if (!categories.length) return [];

    // Filter out duplicates by catId if any exist in the API response
    const seenIds = new Set();
    const uniqueCats = categories.filter((cat) => {
      if (!cat.catId || seenIds.has(cat.catId)) return false;
      seenIds.add(cat.catId);
      return true;
    });

    return uniqueCats.map((cat) => {
      const meta = CATEGORY_MAP_BY_ID[cat.catId];

      const displayName = meta
        ? meta.displayName
        : (cat.catName || "").split("/")[0].trim();

      let bgImage = meta ? meta.bgImage : null;
      if (!bgImage) {
        const lower = (cat.catName || "").toLowerCase();
        if (lower.includes("health") && lower.includes("fitness")) {
          bgImage = "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=600&q=80";
        } else if (lower.includes("fitness") || lower.includes("wellness") || lower.includes("coach")) {
          bgImage = "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=600&q=80";
        } else if (cat.iconUrl && !cat.iconUrl.toLowerCase().endsWith(".svg")) {
          bgImage = cat.iconUrl.startsWith("http")
            ? cat.iconUrl
            : `${config.url}/categoryIcons/${cat.iconUrl}`;
        } else {
          bgImage = DEFAULT_IMAGE;
        }
      }

      // Exact count priority: live fetched count > category API count > verified database map
      const exactCount =
        liveCounts[cat.catId] !== undefined
          ? liveCounts[cat.catId]
          : cat.bookCount !== undefined && cat.bookCount !== null
          ? cat.bookCount
          : meta?.exactCount !== undefined
          ? meta.exactCount
          : 0;

      const priority = meta ? meta.priority : 99;

      return {
        ...cat,
        displayName,
        bgImage,
        exactCount,
        bookCountText: formatBookCount(exactCount),
        priority,
      };
    }).sort((a, b) => a.priority - b.priority);
  }, [categories, liveCounts]);

  const handleCategoryClick = (cat) => {
    navigate("/explore-books", { state: { category: cat } });
  };

  const visibleCategories = showAll
    ? enhancedCategories
    : enhancedCategories.slice(0, 12);

  if (loading) {
    return (
      <section className={`py-4 mb-4 ${className}`}>
        <div className="flex justify-between items-center mb-3 px-1">
          <div className="h-7 w-48 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-16 bg-gray-200 animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="aspect-[16/10] rounded-xl sm:rounded-2xl bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!enhancedCategories.length) {
    return null;
  }

  return (
    <section className={`py-4 mb-6 ${className}`}>
      {/* Header matching BooksAtDoorstep design language */}
      <div className="flex justify-between items-center mb-4 px-1">
        <h2 className="text-2xl md:text-3xl font-bold text-text-muted border-b-2 border-primary pb-1">
          Browse by Category
        </h2>

        <button
          onClick={() => navigate("/explore-books")}
          className="flex items-center gap-2 text-primary hover:text-primary-shade4 transition-colors group"
          aria-label="See all categories"
        >
          <span className="font-medium text-sm sm:text-base">See All</span>
          <div className="flex -space-x-2 overflow-hidden">
            {[0, 0.15].map((delay, i) => (
              <motion.svg
                key={i}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="w-4 h-4 sm:w-5 sm:h-5"
                animate={{ x: [0, 5, 0] }}
                transition={{
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 0.6,
                  delay,
                  ease: "easeInOut",
                }}
              >
                <path
                  d="M9 18L15 12L9 6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            ))}
          </div>
        </button>
      </div>

      {/* Cards Grid: Compact to show multiple categories in one view */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
        {visibleCategories.map((cat, index) => (
          <motion.div
            key={cat.catId || index}
            onClick={() => handleCategoryClick(cat)}
            whileHover={{ y: -3, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="group relative aspect-[16/10] overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 border border-black/5 bg-gray-900"
          >
            {/* Background Photographic Image */}
            <img
              src={cat.bgImage}
              alt={cat.displayName}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_IMAGE;
              }}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />

            {/* Dark Gradient Overlay for Maximum Text Clarity */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 group-hover:from-black/95 group-hover:via-black/75 group-hover:to-black/40 transition-colors duration-300" />

            {/* Subtle Accent Glow on Hover */}
            <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/60 rounded-xl sm:rounded-2xl transition-colors duration-300 pointer-events-none" />

            {/* Card Content at Bottom Left */}
            <div className="absolute inset-0 p-2.5 sm:p-3 flex flex-col justify-end pointer-events-none transition-all duration-300">
              <h3 className="text-white font-bold text-xs sm:text-sm md:text-base leading-snug drop-shadow-md group-hover:text-primary transition-all duration-200 line-clamp-1 group-hover:line-clamp-none break-words">
                {cat.displayName}
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-300/95 font-medium mt-0.5 tracking-wide drop-shadow">
                {cat.bookCountText}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* View More / Show Less Toggle Button */}
      {enhancedCategories.length > 12 && (
        <div className="flex justify-center mt-5">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-white hover:bg-orange-50/80 text-gray-700 hover:text-primary font-medium text-xs sm:text-sm rounded-full border border-gray-200 hover:border-primary/40 shadow-xs hover:shadow-md transition-all duration-200"
          >
            <span>
              {showAll
                ? "Show Fewer Categories"
                : `View All Categories (${enhancedCategories.length})`}
            </span>
            {showAll ? (
              <ChevronUp className="w-4 h-4 text-primary" />
            ) : (
              <ChevronDown className="w-4 h-4 text-primary" />
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default CategoriesSection;